import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  ogImage,
  schema,
}: SEOProps) => {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const currentUrl = `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
      updateMetaTag('property', 'og:title', title);
      updateMetaTag('property', 'twitter:title', title);
    }

    // Update description
    if (description) {
      updateMetaTag('name', 'description', description);
      updateMetaTag('property', 'og:description', description);
      updateMetaTag('property', 'twitter:description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('name', 'keywords', keywords);
    }

    // Update OG image
    if (ogImage) {
      updateMetaTag('property', 'og:image', ogImage);
      updateMetaTag('property', 'twitter:image', ogImage);
    }

    // Update canonical URL
    updateLinkTag('canonical', currentUrl);
    updateMetaTag('property', 'og:url', currentUrl);
    updateMetaTag('property', 'twitter:url', currentUrl);

    // Update last modified date
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('name', 'last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      updateSchema(schema);
    }
  }, [title, description, keywords, ogImage, schema, currentUrl]);
};

const updateMetaTag = (attr: string, attrValue: string, content: string) => {
  let element = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, attrValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
};

const updateSchema = (schema: object) => {
  const schemaId = 'schema-org-jsonld';
  let scriptTag = document.getElementById(schemaId);
  
  if (!scriptTag) {
    scriptTag = document.createElement('script');
    scriptTag.id = schemaId;
    scriptTag.type = 'application/ld+json';
    document.head.appendChild(scriptTag);
  }
  
  scriptTag.textContent = JSON.stringify(schema);
};
